import { getEnvRuntimeVariable, removeDiacritics } from '@if/core/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchInstitutions } from '../api/flinks-institutions-api';
import { FLINKS_INSTITUTIONS } from '../data';
/** The grid never renders more than this, whether searching or not. */
export const INSTITUTION_DISPLAY_LIMIT = 25;
/** Below this many characters a keyword search returns too much noise to be useful. */
const MIN_SEARCH_LENGTH = 3;
/** Each keystroke costs a request, so wait for the user to pause before searching. */
const SEARCH_DEBOUNCE_MS = 500;
const normalize = (value) => removeDiacritics(value).toLowerCase().trim();
/**
 * Names that identify a business/commercial banking portal rather than a personal one.
 *
 * Flinks lists both side by side ("Tangerine" and "Tangerine Business Banking"), and only
 * personal accounts are relevant to a consumer credit application.
 *
 * These are deliberately narrow to avoid catching retail institutions whose legal name
 * happens to contain a business-sounding word — `American Express`, `First Commercial Bank`
 * and `connectFirst CU` must all survive this filter.
 */
const BUSINESS_NAME_PATTERNS = [
    /\bbusiness\b/,
    /\benterprises?\b/,
    /\bentreprises?\b/,
    /\baffaires\b/,
    /\bpme\b/,
    /\bcommerciaux\b/,
    /commercial banking/,
    /\(commercial/,
    /\(corporate/,
    /^rbc express$/,
];
/** Every curated entry Flinks also knows about, so a bank is never listed twice. */
const CURATED_FLINKS_IDS = new Set(FLINKS_INSTITUTIONS.map((institution) => institution.flinksId).filter((id) => id !== undefined));
const useDebouncedValue = (value, delayMs) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timeoutId = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timeoutId);
    }, [value, delayMs]);
    return debounced;
};
const isBusinessInstitution = (name) => BUSINESS_NAME_PATTERNS.some((pattern) => pattern.test(normalize(name)));
const toSelectableInstitution = (dto) => ({
    shortName: String(dto.id),
    name: dto.brandName || dto.name || '',
    // Flinks returns a complete `data:` URI, or an empty string when it has no logo.
    logo: dto.logo || null,
});
/**
 * Builds the institution list shown on the Select-Bank page.
 *
 * The curated `FLINKS_INSTITUTIONS` list is always searched first and its matches keep the
 * top of the grid — they carry our own branding assets and French names. Institutions Flinks
 * knows about beyond that list fill whatever room is left, and the whole thing is capped at
 * `INSTITUTION_DISPLAY_LIMIT`.
 */
export const useInstitutionSearch = (search, financingProgram) => {
    const { i18n } = useTranslation('shared');
    const language = i18n.language === 'fr' ? 'fr' : 'en';
    // Sandbox institutions (e.g. Flinks Capital) only work against the demo Flinks endpoint,
    // so they are hidden unless the app is running in demo/non-production mode.
    const isDemoFlinks = getEnvRuntimeVariable(import.meta.env.VITE_USE_DEMO_FLINKS);
    const query = normalize(search);
    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    const keyword = debouncedQuery.length >= MIN_SEARCH_LENGTH ? debouncedQuery : '';
    const [searchResults, isSearching] = useSearchInstitutions(keyword, language, financingProgram);
    const institutions = useMemo(() => {
        const institutionName = (institution) => language === 'fr' ? institution.nameFr : institution.nameEn;
        const available = FLINKS_INSTITUTIONS.filter((institution) => isDemoFlinks || !institution.devOnly);
        const curatedMatches = query
            ? available.filter((institution) => normalize(institutionName(institution)).includes(query))
            : available;
        const merged = curatedMatches.map((institution) => ({
            shortName: institution.shortName,
            name: institutionName(institution),
            logo: institution.logo,
        }));
        for (const dto of searchResults ?? []) {
            if (merged.length >= INSTITUTION_DISPLAY_LIMIT)
                break;
            if (CURATED_FLINKS_IDS.has(dto.id))
                continue;
            const candidate = toSelectableInstitution(dto);
            // A nameless institution cannot be searched for or labelled, so it is never rendered.
            if (!candidate.name || isBusinessInstitution(candidate.name))
                continue;
            merged.push(candidate);
        }
        return merged.slice(0, INSTITUTION_DISPLAY_LIMIT);
    }, [query, language, isDemoFlinks, searchResults]);
    // The debounce window is part of the wait as far as the user is concerned.
    const isLoading = isSearching || (query.length >= MIN_SEARCH_LENGTH && query !== debouncedQuery);
    return { institutions, isLoading };
};
